import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import OperatorDashboardHeader from './operatorDashboard/OperatorDashboardHeader';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import { resolveActiveCustomerContext } from './auth/customerContext';
import { getRecordReturnTarget } from './recordReturnNavigation';
import {
  Form,
  Input,
  Radio,
  Button,
  Row,
  Col,
  Tooltip,
  Card,
  Modal,
  Typography,
  DatePicker,
  Alert,
  Select,
} from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import PrintOperatorChecklist from './pages/PrintOperatorChecklist.jsx';
import dayjs from 'dayjs';
import RecordAttachments from './components/RecordAttachments';
import { ExternalSubmittedFormRecord, PrintExternalSubmittedFormSummary } from './components/ExternalSubmittedFormRecord';
import { DashboardPageShell, DashboardPanel, DashboardPrimaryActionButton } from './components/dashboard';
import './operatorDashboard/operatorDashboard.css';
import './OperatorChecklist.css';

const PFN = (disabled) => (
  <Radio.Group
    className="operator-checklist-pfn-control"
    options={[
      { label: 'Pass', value: 'P' },
      { label: 'Fail', value: 'F' },
      { label: 'N/A', value: 'N' },
    ]}
    optionType="button"
    buttonStyle="solid"
    disabled={disabled}
  />
);

const VISUAL_ITEMS = [
  'Fluid Leaks',
  'Carriage Rollers / Slide Blocks',
  'Mast – Chains / Retainers / Sheaves / Anchors',
  'Forks and Fork Lock Pins',
  'Hydraulic Cylinders',
  'Brake Master Cylinder',
  'Over Mast Hoses and Cables',
  'Caster Assembly',
  'Overhead Guard',
  'Drive Unit / Radial Rings',
  'Data Plate / Capacity Label Legible',
];

const IC_ONLY_ITEMS = [
  'Engine Coolant / Oil / Air Filter',
  'Radiator / Hoses',
  'Propane Tank Mounted & Undamaged',
  'Hood Latch (IC)',
  'Exhaust System Condition / Leaks',
];

const ELECTRIC_ONLY_ITEMS = [
  'Battery Restraints & Condition',
  'Electrolyte/Water As Applicable',
  'Hood Latch (Electric)',
  'Charger Cord / Plug Condition',
];

const OPERATIONAL_ITEMS = [
  'Power-Up & Disconnects',
  'Steering Response',
  'Service Brakes (Travel)',
  'Electrical Safety Features',
  'Hydraulic Lift/Tilt/Attachments',
  'Limit Switches / Interlocks',
];

const PRINT_SECTIONS = [
  { title: '1. Visual Inspection', items: VISUAL_ITEMS },
  { title: '2. Internal Combustion Only', items: IC_ONLY_ITEMS },
  { title: '3. Electric Forklifts Only', items: ELECTRIC_ONLY_ITEMS },
  { title: '4. Operational Inspection', items: OPERATIONAL_ITEMS },
];

const equipmentSpecificFieldName = (label) => `check_${label}`;
const EQUIPMENT_SPECIFIC_FIELD_NAMES = [
  ...IC_ONLY_ITEMS.map(equipmentSpecificFieldName),
  ...ELECTRIC_ONLY_ITEMS.map(equipmentSpecificFieldName),
];

const isSectionComplete = (values, labels) =>
  labels.every((label) => Boolean(values?.[equipmentSpecificFieldName(label)]));

const isSectionStarted = (values, labels) =>
  labels.some((label) => Boolean(values?.[equipmentSpecificFieldName(label)]));

export default function OperatorChecklist() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [qs] = useSearchParams();
  const unitIdFromQuery = qs.get('unitId') || '';
  const recordId = qs.get('id') || null;
  const isSafetyReportMode = qs.get('safetyReport') === '1';

  const isViewMode = !!recordId;
  const recordReturnTarget = getRecordReturnTarget(location, session?.role);

  const [form] = Form.useForm();
  const [externalForm] = Form.useForm();
  const [, setLoading] = useState(true);
  const [forklift, setForklift] = useState(null);
  const [initialHour, setInitialHour] = useState(null);
  const [hourEdited, setHourEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState('');
  const [resultMessage, setResultMessage] = useState('');

  const [isReadOnly, setIsReadOnly] = useState(isViewMode);

  const [isDirty, setIsDirty] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const pendingNavRef = useRef(null);
  const dirtyInitRef = useRef(false);
  const isDirtyRef = useRef(false);
  const unsavedOpenRef = useRef(false);
  const allowNavigationRef = useRef(false);
  const leavingRef = useRef(false);

  // Added for "Performed By" real name
  const [operatorName, setOperatorName] = useState('');
  const [submittedAt, setSubmittedAt] = useState('');
  const [savedRecordId, setSavedRecordId] = useState(null);
  const attachmentsRef = useRef(null);
  const externalAttachmentsRef = useRef(null);
  const [formPath, setFormPath] = useState(recordId ? 'native' : null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [submissionSource, setSubmissionSource] = useState('');
  const [checklistRecordDetail, setChecklistRecordDetail] = useState(null);

  useEffect(() => {
    try {
      localStorage.removeItem('dirtyForm');
    } catch {}
    setIsDirty(false);
  }, []);

  const loadForNew = useCallback(async () => {
    if (!unitIdFromQuery) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/api/forklifts/unit/${encodeURIComponent(unitIdFromQuery)}`
      );
      setForklift(data || null);
      const hm = Number.isFinite(data?.hourMeter)
        ? Number(data.hourMeter)
        : null;
      setInitialHour(hm);
      form.setFieldsValue({
        customer_name: data?.customerName || '',
        address: data?.customerAddress || '',
        city: data?.customerCity || '',
        state: data?.customerState || '',
        unit_id: unitIdFromQuery,
        make: data?.make || '',
        model: data?.model || '',
        serial_number: data?.serialNumber || '',
        hour_meter: hm,
        date: dayjs(),
      });
      setHourEdited(false);
    } catch {
      setForklift(null);
    } finally {
      setLoading(false);
    }
  }, [unitIdFromQuery, form]);

  useEffect(() => {
    if (!isViewMode && unitIdFromQuery) loadForNew();
  }, [isViewMode, unitIdFromQuery, loadForNew]);

  useEffect(() => {
    if (formPath !== 'external') return;
    externalForm.setFieldsValue({
      customer_name: forklift?.customerName || '',
      address: forklift?.customerAddress || '',
      city: forklift?.customerCity || '',
      state: forklift?.customerState || '',
      unit_id: unitIdFromQuery,
      hour_meter: externalForm.getFieldValue('hour_meter') ?? initialHour,
      date: externalForm.getFieldValue('date') || dayjs(),
      result: externalForm.getFieldValue('result') || 'P',
    });
  }, [formPath, externalForm, forklift, unitIdFromQuery, initialHour]);

  // View mode load (pull operator name from API)
  useEffect(() => {
    if (!recordId) return;
    let alive = true;

    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/operator/checklist/${recordId}`);
        if (!alive) return;

        setSubmissionSource(data.submissionSource || 'MANAGELIFT_FORM');

        const forkliftInfo = data.forklift || {};
        setChecklistRecordDetail({ ...data, forklift: forkliftInfo });
        const checks = data.checks || {};

        const checkFields = {};
        PRINT_SECTIONS.forEach((section) => {
          section.items.forEach((label) => {
            if (checks[label]) {
              checkFields[`check_${label}`] = checks[label];
            }
          });
        });

        form.setFieldsValue({
          customer_name: forkliftInfo.customerName || '',
          address: forkliftInfo.address || '',
          city: forkliftInfo.city || '',
          state: forkliftInfo.state || '',
          unit_id: data.unitId || forkliftInfo.unitId || '',
          make: forkliftInfo.make || '',
          model: forkliftInfo.model || '',
          serial_number: forkliftInfo.serialNumber || '',
          hour_meter:
            data.hourMeter ??
            forkliftInfo.currentHourMeter ??
            '',
          date: data.date ? dayjs(data.date) : dayjs(),
          notes: data.notes || '',
          operator_signature: data.operatorSignature || '',
          performed_by: data.operatorName || '',
          ...checkFields,
        });

        setOperatorName(data.operatorName || '');
        setSubmittedAt(data.createdAt || '');

        setForklift(forkliftInfo);
        const hm =
          Number.isFinite(data.hourMeter)
            ? data.hourMeter
            : Number.isFinite(forkliftInfo.currentHourMeter)
            ? forkliftInfo.currentHourMeter
            : null;

        setInitialHour(hm);
        setHourEdited(false);
        setIsReadOnly(true);
        setIsDirty(false);
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
      } catch {
        setForklift(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [recordId, form]);

  const onFinish = async (vals) => {
    if (submitting || isReadOnly) return;
    setSubmitting(true);

    try {
      const checks = PRINT_SECTIONS.flatMap((s) => s.items);
      const inspection = {};
      checks.forEach((label) => {
        inspection[label] = vals[`check_${label}`];
      });

      const payload = {
        forkliftId: forklift?.id || null,
        unitId: vals.unit_id,
        customerName: vals.customer_name,
        address: vals.address,
        city: vals.city,
        state: vals.state,
        hourMeter: vals.hour_meter ? Number(vals.hour_meter) : null,
        date: allVals?.date
          ? dayjs(allVals.date).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        notes: vals.notes || '',
        operatorSignature: vals.operator_signature || '',
        checksJson: JSON.stringify(inspection),
      };

      const resp = await api.post('/api/operator/checklist', payload);
      const createdId = resp?.data?.id || null;
      setSavedRecordId(createdId);
      let uploadOutcome = null;
      if (createdId && attachmentsRef.current?.hasPending?.()) {
        uploadOutcome = await attachmentsRef.current.flushUploads(createdId);
      }

      try {
        localStorage.removeItem('dirtyForm');
      } catch {}

      setIsDirty(false);
      if (uploadOutcome && !uploadOutcome.ok) {
        setResultTitle('Saved with warning');
        setResultMessage(`Operator checklist submitted, but one or more uploads failed: ${uploadOutcome.message || 'Upload failed.'}`);
      } else {
        setResultTitle('Success');
        setResultMessage('Operator checklist submitted.');
      }
      setResultOpen(true);
    } catch (e) {
      const message =
        e?.data?.message ||
        e?.response?.data?.message ||
        e?.message ||
        'Failed to submit operator checklist.';
      setResultTitle('Error');
      setResultMessage(message);
      setResultOpen(true);
    } finally {
      setSubmitting(false);
    }
  };



  const handleNativeFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return;

    const first = errorFields[0].name;
    form.scrollToField(first, { behavior: 'smooth', block: 'center' });

    requestAnimationFrame(() => {
      const idSuffix = Array.isArray(first) ? first.join('_') : first;
      const el =
        document.querySelector(`[id$="${idSuffix}"]`) ||
        form.getFieldInstance?.(first);

      if (el && typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      }
    });
  };

  const onExternalFinish = async (vals) => {
    if (submitting || isReadOnly) return;
    const externalFiles = externalAttachmentsRef.current?.getPendingFiles?.('EXTERNAL_SUBMITTED_FORM') || [];
    if (externalFiles.length < 1 || externalFiles.length > 3) {
      setResultTitle('External form required');
      setResultMessage('Upload at least one and no more than three external submitted checklist files/pages before submitting.');
      setResultOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = vals.result || 'P';
      const payload = {
        forkliftId: forklift?.id || null,
        unitId: vals.unit_id,
        customerName: vals.customer_name,
        address: vals.address,
        city: vals.city,
        state: vals.state,
        hourMeter: vals.hour_meter ? Number(vals.hour_meter) : null,
        date: vals.date ? dayjs(vals.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        notes: vals.notes || '',
        operatorSignature: vals.operator_signature || '',
        checksJson: JSON.stringify({ 'External submitted checklist': result }),
        submissionSource: 'EXTERNAL_FORM',
      };
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      externalFiles.forEach((file) => fd.append('files', file));
      const resp = await api.post('/api/operator/checklist/external-form', fd);
      const createdId = resp?.data?.id || null;
      setSavedRecordId(createdId);
      if (createdId) {
        const photoResult = await externalAttachmentsRef.current?.flushUploads?.(createdId, 'PHOTO');
        if (photoResult && !photoResult.ok) {
          setResultTitle('Photo upload failed');
          setResultMessage(photoResult.message || 'External operator checklist was saved, but photo attachments failed to upload.');
          setResultOpen(true);
          return;
        }
      }
      setResultTitle('Success');
      setResultMessage('External operator checklist submitted.');
      setResultOpen(true);
    } catch (e) {
      setResultTitle('Error');
      setResultMessage(e?.response?.data?.message || e?.message || 'Failed to submit external operator checklist.');
      setResultOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const allVals = Form.useWatch([], form);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    unsavedOpenRef.current = unsavedOpen;
  }, [unsavedOpen]);

  useEffect(() => {
    if (isReadOnly) return;
    if (!allVals || Object.keys(allVals).length === 0) return;
    if (!dirtyInitRef.current) {
      dirtyInitRef.current = true;
      return;
    }
    setIsDirty(true);
    try {
      localStorage.setItem('dirtyForm', '1');
    } catch {}
  }, [allVals, isReadOnly]);

  const requestLeave = useCallback(
    (action) => {
      if (!isDirtyRef.current || isReadOnly || allowNavigationRef.current) {
        action();
        return;
      }
      if (unsavedOpenRef.current) return;
      pendingNavRef.current = action;
      unsavedOpenRef.current = true;
      setUnsavedOpen(true);
    },
    [isReadOnly]
  );

  const handleStayOnPage = useCallback(() => {
    pendingNavRef.current = null;
    leavingRef.current = false;
    allowNavigationRef.current = false;
    unsavedOpenRef.current = false;
    setUnsavedOpen(false);
  }, []);

  const clearDirtyState = useCallback(() => {
    try {
      localStorage.removeItem('dirtyForm');
    } catch {}
    isDirtyRef.current = false;
    setIsDirty(false);
  }, []);

  const handleLeavePage = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;

    const action = pendingNavRef.current;
    pendingNavRef.current = null;
    allowNavigationRef.current = true;
    clearDirtyState();
    unsavedOpenRef.current = false;
    setUnsavedOpen(false);

    if (action) {
      action();
    }
  }, [clearDirtyState]);

  const handleCancel = () => {
    requestLeave(() => navigate(recordReturnTarget, { replace: true }));
  };

  useEffect(() => {
    if (isReadOnly) return;
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current || allowNavigationRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isReadOnly]);

  useEffect(() => {
    if (!isDirty || isReadOnly) return;

    const currentUrl = window.location.href;

    const handlePopState = () => {
      if (allowNavigationRef.current) return;
      window.history.pushState(null, '', currentUrl);
      requestLeave(() => {
        allowNavigationRef.current = true;
        navigate(-2);
      });
    };

    window.history.pushState(null, '', currentUrl);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, isReadOnly, navigate, requestLeave]);

  const renderPathChoice = () => (
    <DashboardPageShell
      title={forklift?.customerName || resolveActiveCustomerContext(session).customerName}
      productDashboardShell="operator"
      renderHeaderActions={({ activeCustomerName, logout, navigate, session }) => (
        <OperatorDashboardHeader
          accountMenuOpen={accountMenuOpen}
          customerName={activeCustomerName}
          logout={logout}
          navigate={navigate}
          onAccountMenuOpenChange={setAccountMenuOpen}
          session={session}
        />
      )}
      headerClassName="operator-dashboard-header operator-checklist-choice-header"
      contentClassName="operator-checklist-choice-page"
    >
      <DashboardPanel className="operator-dashboard-panel operator-checklist-choice-panel">
        <div className="operator-checklist-choice-heading">
          <Typography.Title level={3}>
            Submit operator checklist
          </Typography.Title>
          <Typography.Paragraph className="operator-checklist-choice-helper">
            Choose a checklist option to continue.
          </Typography.Paragraph>
        </div>

        <div
          className="operator-checklist-choice-options"
          aria-label="Operator checklist submission options"
        >
          <article className="operator-checklist-choice-card operator-checklist-choice-card-featured">
            <div className="operator-checklist-choice-card-copy">
              <span
                className="operator-checklist-choice-icon operator-checklist-choice-icon-primary"
                aria-hidden="true"
              >
                <CheckCircleOutlined />
              </span>
              <div>
                <Typography.Text className="operator-checklist-choice-card-eyebrow">
                  Recommended
                </Typography.Text>
                <Typography.Title level={3}>Use ManageLift form</Typography.Title>
                <Typography.Paragraph>
                  Complete the built-in digital checklist in ManageLift for this unit.
                </Typography.Paragraph>
              </div>
            </div>
            <DashboardPrimaryActionButton
              size="large"
              onClick={() => setFormPath('native')}
              className="operator-checklist-choice-button operator-checklist-choice-primary"
            >
              Start digital checklist
              <ArrowRightOutlined />
            </DashboardPrimaryActionButton>
          </article>

          <article className="operator-checklist-choice-card">
            <div className="operator-checklist-choice-card-copy">
              <span className="operator-checklist-choice-icon" aria-hidden="true">
                <UploadOutlined />
              </span>
              <div>
                <Typography.Text className="operator-checklist-choice-card-eyebrow">
                  External form
                </Typography.Text>
                <Typography.Title level={3}>Use my own form</Typography.Title>
                <Typography.Paragraph>
                  Upload or submit a completed external operator checklist instead.
                </Typography.Paragraph>
              </div>
            </div>
            <Button
              size="large"
              onClick={() => setFormPath('external')}
              className="operator-checklist-choice-button operator-checklist-choice-secondary"
            >
              Submit external form
              <ArrowRightOutlined />
            </Button>
          </article>
        </div>

        <div className="operator-checklist-choice-footer">
          <Typography.Text className="operator-checklist-choice-footer-text">
            Not ready to submit? Return to the operator dashboard.
          </Typography.Text>
          <Button
            size="large"
            onClick={handleCancel}
            className="operator-checklist-choice-cancel"
          >
            Cancel
          </Button>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );

  const renderExternalForm = () => (
    <>
      <DashboardPageShell
        title={forklift?.customerName || resolveActiveCustomerContext(session).customerName}
        productDashboardShell="operator"
        renderHeaderActions={({ activeCustomerName, logout, navigate, session }) => (
          <OperatorDashboardHeader
            accountMenuOpen={accountMenuOpen}
            customerName={activeCustomerName}
            logout={logout}
            navigate={navigate}
            onAccountMenuOpenChange={setAccountMenuOpen}
            session={session}
          />
        )}
        headerClassName="operator-dashboard-header operator-checklist-form-header"
        contentClassName="operator-checklist-form-page"
        contentInnerClassName="operator-checklist-form-shell"
      >
        <DashboardPanel className="operator-dashboard-panel operator-checklist-form-panel operator-checklist-external-panel">
          <div className="operator-checklist-form-heading">
            <div className="operator-checklist-title-group">
              <Typography.Title level={2}>Submit external checklist</Typography.Title>
              <Typography.Paragraph className="operator-checklist-form-helper">
                Upload the completed checklist, then add any optional lift or work photos.
              </Typography.Paragraph>
            </div>
          </div>

          <Card className="operator-checklist-section-card operator-checklist-external-card" title="Checklist details">
            <Form
              form={externalForm}
              layout="vertical"
              onFinish={onExternalFinish}
              className="operator-checklist-native-form operator-checklist-external-form"
              initialValues={{ customer_name: forklift?.customerName || '', address: forklift?.customerAddress || '', city: forklift?.customerCity || '', state: forklift?.customerState || '', unit_id: unitIdFromQuery, hour_meter: initialHour, date: dayjs(), result: 'P' }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item label="Customer" name="customer_name"><Input disabled /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Unit ID" name="unit_id" rules={[{ required: true, message: 'Unit ID required' }]}><Input disabled /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Date" name="date" rules={[{ required: true, message: 'Date required' }]}><DatePicker className="operator-checklist-date-picker" allowClear={false} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Hour Meter" name="hour_meter" rules={[{ required: true, message: 'Hour meter required' }]}><Input type="number" min={initialHour ?? 0} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Checklist Result" name="result" rules={[{ required: true, message: 'Result required' }]}><Select options={[{ value: 'P', label: 'Passed / in service' }, { value: 'F', label: 'Failed / mark down' }]} /></Form.Item></Col>
                <Col xs={24}><Form.Item label="Notes / Summary" name="notes"><Input.TextArea rows={4} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Operator Signature" name="operator_signature" rules={[{ required: true, message: 'Signature required' }]}><Input /></Form.Item></Col>
              </Row>
              <div className="operator-checklist-external-attachments">
                <RecordAttachments ref={externalAttachmentsRef} recordType="OPERATOR_CHECKLIST" recordId={savedRecordId} readOnly={false} attachmentTypes={['EXTERNAL_SUBMITTED_FORM', 'PHOTO']} variant="uploadSection" />
              </div>
              <div className="operator-checklist-actions operator-checklist-external-actions">
                <DashboardPrimaryActionButton
                  htmlType="submit"
                  loading={submitting}
                  className="operator-checklist-submit"
                >
                  Submit external checklist
                </DashboardPrimaryActionButton>
                <Button
                  onClick={handleCancel}
                  disabled={submitting}
                  className="dashboard-secondary-action-button operator-checklist-cancel"
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card>
        </DashboardPanel>
      </DashboardPageShell>

      <Modal
        open={resultOpen}
        title={resultTitle}
        onOk={() => { setResultOpen(false); navigate(recordReturnTarget, { replace: true }); }}
        onCancel={() => { setResultOpen(false); navigate(recordReturnTarget, { replace: true }); }}
        okText="OK"
        cancelButtonProps={{ className: 'operator-checklist-modal-hidden-button' }}
      >
        <Typography.Paragraph className="operator-checklist-modal-message">{resultMessage}</Typography.Paragraph>
      </Modal>
    </>
  );

  if (!isReadOnly && !recordId && !formPath) return renderPathChoice();
  if (!isReadOnly && !recordId && formPath === 'external') return renderExternalForm();

  const isExternalFormRecord = isReadOnly && submissionSource === 'EXTERNAL_FORM';
  const formatMetadataDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
  const formatMetadataDateTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '');
  const checklistDetail = checklistRecordDetail || {};
  const checklistForklift = checklistDetail.forklift || {};
  const checklistMetadata = [
    { label: 'Customer', value: isExternalFormRecord ? checklistForklift.customerName || '' : allVals?.customer_name || '' },
    { label: 'Unit ID', value: isExternalFormRecord ? checklistDetail.unitId || checklistForklift.unitId || unitIdFromQuery || '' : allVals?.unit_id || unitIdFromQuery || '' },
    { label: 'Make', value: isExternalFormRecord ? checklistForklift.make || '' : allVals?.make || '' },
    { label: 'Model', value: isExternalFormRecord ? checklistForklift.model || '' : allVals?.model || '' },
    { label: 'Serial Number', value: isExternalFormRecord ? checklistForklift.serialNumber || '' : allVals?.serial_number || '' },
    { label: 'Date', value: formatMetadataDate(isExternalFormRecord ? checklistDetail.date : allVals?.date) },
    { label: 'Hour Meter', value: isExternalFormRecord ? checklistDetail.hourMeter ?? checklistForklift.currentHourMeter ?? '' : allVals?.hour_meter ?? '' },
    { label: 'Notes / Summary', value: isExternalFormRecord ? checklistDetail.notes || '' : allVals?.notes || '' },
    { label: 'Operator Signature', value: isExternalFormRecord ? checklistDetail.operatorSignature || '' : allVals?.operator_signature || '' },
    { label: 'Submitted At', value: formatMetadataDateTime(isExternalFormRecord ? checklistDetail.createdAt : submittedAt) },
  ];

  return (
    <>
      <div className="screen-only">
        <DashboardPageShell
          title={forklift?.customerName || resolveActiveCustomerContext(session).customerName}
          productDashboardShell="operator"
          renderHeaderActions={({ activeCustomerName, logout, navigate, session }) => (
            <OperatorDashboardHeader
              accountMenuOpen={accountMenuOpen}
              customerName={activeCustomerName}
              logout={logout}
              navigate={navigate}
              onAccountMenuOpenChange={setAccountMenuOpen}
              session={session}
            />
          )}
          headerClassName="operator-dashboard-header operator-checklist-form-header"
          contentClassName="operator-checklist-form-page"
          contentInnerClassName={`operator-checklist-form-shell${isReadOnly ? ' operator-checklist-readonly-form-shell' : ''}`}
        >
          <DashboardPanel className={`operator-dashboard-panel operator-checklist-form-panel${isReadOnly ? ' operator-checklist-readonly-form-panel' : ''}`}>
          <div className="operator-checklist-form-heading">
            <div className="operator-checklist-title-group">
              <Typography.Title level={2}>{isExternalFormRecord ? 'Submitted Operator Checklist' : 'Operator Checklist'}</Typography.Title>
              <Typography.Paragraph className="operator-checklist-form-helper">
                {isExternalFormRecord
                  ? 'Review the submitted external form, captured record details, and related attachments.'
                  : isReadOnly
                    ? 'Submitted operator checklist record.'
                    : 'Confirm the unit details, complete the required inspection groups, and submit the operator checklist.'}
              </Typography.Paragraph>
            </div>
          </div>

            {isExternalFormRecord ? (
              <ExternalSubmittedFormRecord
                formName="Operator Checklist"
                recordType="OPERATOR_CHECKLIST"
                recordId={recordId || savedRecordId}
                metadata={checklistMetadata}
                performedByLabel="Operator"
                performedByName={operatorName}
                onPrint={() => window.print()}
                onCancel={handleCancel}
              />
            ) : (
            <>

            {isSafetyReportMode && !isReadOnly && (
              <Alert
                type="warning"
                showIcon
                message="Required maintenance is overdue"
                description="This unit is unavailable for normal operator use. Failed safety reports are still accepted so hazards can be reported and the forklift can be marked down."
                className="operator-checklist-alert"
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={handleNativeFinishFailed}
              className="operator-checklist-native-form"
            >
              <Card className="operator-checklist-section-card" title="Equipment & Customer">
                <Row gutter={[16, 4]} className="eq-print-grid">
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Customer Name" name="customer_name">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Address" name="address">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="City" name="city">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="State" name="state">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Date" name="date" required>
                      <DatePicker
                        className="operator-checklist-date-picker"
                        disabled
                        allowClear={false}
                        disabledDate={(current) => {
                          if (!current) return false;
                          const today = dayjs().startOf('day');
                          return current.startOf('day').isBefore(today) ||
                                 current.startOf('day').isAfter(today);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Unit ID#" name="unit_id">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Make" name="make">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Model" name="model">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Serial Number" name="serial_number">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Performed By" name="performed_by">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={
                        <span>
                          Hour Meter{' '}
                          <Tooltip title={`Current: ${initialHour ?? '—'}`}>
                            <InfoCircleOutlined className="operator-checklist-info-icon" />
                          </Tooltip>
                        </span>
                      }
                      name="hour_meter"
                      rules={
                        isReadOnly
                          ? []
                          : [
                              { required: true, message: 'Hour meter required' },
                              {
                                validator: (_, value) => {
                                  if (!hourEdited) {
                                    return Promise.reject(
                                      new Error('Confirm the current hour meter')
                                    );
                                  }
                                  const num = Number(value);
                                  if (!Number.isFinite(num)) {
                                    return Promise.reject(
                                      new Error('Enter a number')
                                    );
                                  }
                                  if (
                                    initialHour != null &&
                                    num < initialHour
                                  ) {
                                    return Promise.reject(
                                      new Error(`Must be ≥ ${initialHour}`)
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]
                      }
                    >
                      <Input
                        type="number"
                        inputMode="numeric"
                        onFocus={() => setHourEdited(true)}
                        onChange={() => setHourEdited(true)}
                        disabled={isReadOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card className="operator-checklist-section-card operator-checklist-inspection-card" title="1. Visual Inspection">
                <Row gutter={[16, 8]}>
                  {VISUAL_ITEMS.map((label, i) => (
                    <Col xs={24} md={12} lg={8} key={i}>
                      <Form.Item
                        label={label}
                        name={`check_${label}`}
                        rules={isReadOnly ? [] : [{ required: true }]}
                      >
                        {PFN(isReadOnly)}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Card>

              <Form.Item
                name="equipment_specific_validation"
                dependencies={EQUIPMENT_SPECIFIC_FIELD_NAMES}
                rules={
                  isReadOnly
                    ? []
                    : [
                        ({ getFieldValue }) => ({
                          validator: () => {
                            const values = Object.fromEntries(
                              EQUIPMENT_SPECIFIC_FIELD_NAMES.map((name) => [name, getFieldValue(name)])
                            );
                            const icStarted = isSectionStarted(values, IC_ONLY_ITEMS);
                            const electricStarted = isSectionStarted(values, ELECTRIC_ONLY_ITEMS);
                            const icComplete = isSectionComplete(values, IC_ONLY_ITEMS);
                            const electricComplete = isSectionComplete(values, ELECTRIC_ONLY_ITEMS);

                            if ((icStarted && !icComplete) || (electricStarted && !electricComplete)) {
                              return Promise.reject(
                                new Error('Finish the applicable IC-only or electric-only section before submitting.')
                              );
                            }

                            if (icComplete || electricComplete) {
                              return Promise.resolve();
                            }

                            return Promise.reject(
                              new Error('Complete the applicable IC-only or electric-only section before submitting.')
                            );
                          },
                        }),
                      ]
                }
              >
                <Input type="hidden" className="operator-checklist-hidden-validation" />
              </Form.Item>

              <Row gutter={[16, 14]}>
                <Col xs={24} lg={12}>
                  <Card className="operator-checklist-section-card operator-checklist-inspection-card" title="2. Internal Combustion Only">
                    <Row gutter={[16, 8]}>
                      {IC_ONLY_ITEMS.map((label, i) => (
                        <Col xs={24} md={12} key={i}>
                          <Form.Item
                            label={label}
                            name={`check_${label}`}
                            rules={[]}
                          >
                            {PFN(isReadOnly)}
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card className="operator-checklist-section-card operator-checklist-inspection-card" title="3. Electric Forklifts Only">
                    <Row gutter={[16, 8]}>
                      {ELECTRIC_ONLY_ITEMS.map((label, i) => (
                        <Col xs={24} md={12} key={i}>
                          <Form.Item
                            label={label}
                            name={`check_${label}`}
                            rules={[]}
                          >
                            {PFN(isReadOnly)}
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                </Col>
              </Row>

              <Card className="operator-checklist-section-card operator-checklist-inspection-card" title="4. Operational Inspection">
                <Row gutter={[16, 8]}>
                  {OPERATIONAL_ITEMS.map((label, i) => (
                    <Col xs={24} md={12} lg={8} key={i}>
                      <Form.Item
                        label={label}
                        name={`check_${label}`}
                        rules={isReadOnly ? [] : [{ required: true }]}
                      >
                        {PFN(isReadOnly)}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Card>

              <Card className="operator-checklist-section-card" title="Notes & Operator Signature">
                <Form.Item label="Notes" name="notes">
                  <Input.TextArea rows={4} disabled={isReadOnly} />
                </Form.Item>
                <div className={`operator-checklist-signature-row${isReadOnly ? ' operator-checklist-readonly-signature-row' : ''}`}>
                  <Form.Item
                    label="Operator Signature"
                    name="operator_signature"
                    rules={isReadOnly ? [] : [{ required: true }]}
                    className="operator-checklist-signature-item"
                  >
                    <Input
                      disabled={isReadOnly}
                    />
                  </Form.Item>
                </div>
              </Card>

              {!isReadOnly && (
                <div className="operator-checklist-actions">
                  <DashboardPrimaryActionButton
                    htmlType="submit"
                    loading={submitting}
                    disabled={submitting}
                    className="operator-checklist-submit"
                  >
                    Submit
                  </DashboardPrimaryActionButton>
                  <Button
                    onClick={handleCancel}
                    disabled={submitting}
                    className="operator-checklist-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </Form>
            {isReadOnly ? (
              <Card className="operator-checklist-section-card operator-checklist-readonly-attachments-card" title="Attachments">
                <div className="operator-checklist-readonly-attachments">
                  <section className="operator-checklist-readonly-attachment-single" aria-labelledby="operator-photo-attachments-heading">
                    <h3 id="operator-photo-attachments-heading">Photos / Attachments</h3>
                    <RecordAttachments
                      ref={attachmentsRef}
                      recordType="OPERATOR_CHECKLIST"
                      recordId={recordId || savedRecordId}
                      readOnly={true}
                      attachmentTypes={['PHOTO']}
                      variant="compactReadOnlySection"
                      onUploadFailed={(backendMessage) => Modal.warning({ title: 'Record saved with warning', content: `Record was saved, but one or more uploads failed: ${backendMessage || 'Upload failed.'}` })}
                    />
                  </section>
                </div>
              </Card>
            ) : (
              <div className="operator-checklist-editable-attachments">
                <RecordAttachments ref={attachmentsRef} recordType="OPERATOR_CHECKLIST" recordId={recordId || savedRecordId} readOnly={isReadOnly} attachmentTypes={['PHOTO']} onUploadFailed={(backendMessage) => Modal.warning({ title: 'Record saved with warning', content: `Record was saved, but one or more uploads failed: ${backendMessage || 'Upload failed.'}` })} />
              </div>
            )}
            {isReadOnly && (
              <div className="operator-checklist-readonly-actions">
                <DashboardPrimaryActionButton onClick={() => window.print()} className="operator-checklist-readonly-action operator-checklist-readonly-print-action">
                  Print
                </DashboardPrimaryActionButton>
                <Button onClick={handleCancel} className="dashboard-secondary-action-button operator-checklist-readonly-action">
                  Cancel
                </Button>
              </div>
            )}
            </>
            )}
          </DashboardPanel>
        </DashboardPageShell>
      </div>

      {/* PRINT VIEW */}
      <div className="print-only" aria-hidden="true">
        {isExternalFormRecord ? (
            <PrintExternalSubmittedFormSummary
              formName="Operator Checklist"
              metadata={checklistMetadata}
              performedByLabel="Operator"
              performedByName={operatorName}
            />
          ) : (
          <PrintOperatorChecklist
            header={{
              customerName: allVals?.customer_name || '',
              address: allVals?.address || '',
              city: allVals?.city || '',
              state: allVals?.state || '',
              date: allVals?.date
                ? dayjs(allVals.date).startOf('day').format('YYYY-MM-DD')
                : dayjs().startOf('day').format('YYYY-MM-DD'),
              unitId: allVals?.unit_id || unitIdFromQuery || '',
              make: allVals?.make || '',
              model: allVals?.model || '',
              serialNumber: allVals?.serial_number || '',
              hourMeter: allVals?.hour_meter ?? '',
              notes: allVals?.notes || '',
              operatorSignature: allVals?.operator_signature || '',
              operatorName: operatorName || '',
              submittedAt: submittedAt || '',
              recordId: recordId || savedRecordId || '',
            }}
            sections={PRINT_SECTIONS}
            pfn={Object.fromEntries(
              Object.keys(allVals || {}).map((k) => [k, allVals[k]])
            )}
          />
          )}
      </div>

      <Modal
        open={resultOpen}
        title={resultTitle}
        onOk={() => {
          setResultOpen(false);
          navigate(recordReturnTarget, { replace: true });
        }}
        onCancel={() => {
          setResultOpen(false);
          navigate(recordReturnTarget, { replace: true });
        }}
        okText="OK"
        cancelButtonProps={{ className: 'operator-checklist-modal-hidden-button' }}
      >
        <Typography.Paragraph className="operator-checklist-modal-message">
          {resultMessage}
        </Typography.Paragraph>
      </Modal>

      <Modal
        open={unsavedOpen}
        title="Unsaved Changes"
        onCancel={handleStayOnPage}
        footer={[
          <Button
            key="stay"
            type="primary"
            className="operator-checklist-modal-action"
            onClick={handleStayOnPage}
          >
            Stay on Page
          </Button>,
          <Button
            key="leave"
            type="primary"
            danger
            className="operator-checklist-modal-action"
            onClick={handleLeavePage}
          >
            Leave Page
          </Button>,
        ]}
        centered
        maskClosable={false}
      >
        You have unsaved changes on this form. If you leave this page, your entries
        will not be saved.
      </Modal>
    </>
  );
}
